import { formatHSCode } from '@app/utils/general';
import { Button, Input, Pulse } from 'easyship-components';
import { ChevronBackward, CircleCross, Search } from 'easyship-components/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';
import { useCategoryHSCodeContext } from './context/context';
import { useHSCodeSearchValidation } from './hooks/useHSCodesSearchValidation';
import {
  CategoryHSCodeSearchProps,
  CategoryHSCodeSuggested,
  CategoryHSOption,
  CategoryHSSuggestedOption,
  PortalVerticalDirection,
} from './types';

const DEBOUNCE_TIME = 600;

function HighlightedHSCode({ code, searchTerm }: { code: string; searchTerm: string }) {
  if (!searchTerm) return <span>{formatHSCode(code)}</span>;

  const formattedCode = formatHSCode(code);

  const cleanSearchTerm = searchTerm.replace(/\./g, '');

  const pattern = cleanSearchTerm.split('').join('\\.?');
  const regex = new RegExp(`(${pattern})`, 'i');

  const parts = formattedCode.split(regex);

  return (
    <span>
      {parts.map((part, index) => {
        const key = `${part}-${index}`;
        return regex.test(part) ? <strong key={key}>{part}</strong> : <span key={key}>{part}</span>;
      })}
    </span>
  );
}

function TrailingIcon() {
  const { setComponentStateDropdown } = useCategoryHSCodeContext();
  return (
    <CircleCross
      className="cursor-pointer text-sky-700 min-w-6 min-h-6"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setComponentStateDropdown();
      }}
    />
  );
}

export function CategoryHSCodeSearch({ className }: CategoryHSCodeSearchProps) {
  const [value, setValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<CategoryHSSuggestedOption | null>(null);

  const { setComponentStateDropdown, setSelectedValueBySearch, portalPosition } =
    useCategoryHSCodeContext();

  const { hsCodeSuggestions, isLoadingHSCodeSuggestions, setSearchValueDebounced, searchValue } =
    useHSCodeSearchValidation();

  const onSelect = (value: CategoryHSSuggestedOption) => {
    setSelectedValue(value);
    setSelectedValueBySearch(value);
  };

  useEffect(() => {
    const th = setTimeout(() => {
      setSearchValueDebounced(value);
    }, DEBOUNCE_TIME);

    return () => clearTimeout(th);
  }, [value, setSearchValueDebounced]);

  const refInput = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (refInput.current) {
      refInput.current.focus();
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    value = value.replace(/[^0-9.]/g, '');
    value = value.replace(/\.+/g, '.');
    setValue(value);
  };

  const isSearching = searchValue.length > 1;
  const isNoResults = !hsCodeSuggestions?.length && searchValue.length > 1;

  const options = useMemo(() => {
    if (!hsCodeSuggestions?.length) return [];
    return hsCodeSuggestions.map((hsCode) => {
      const label = `${formatHSCode(hsCode.name)} ${
        hsCode.description ? `- ${hsCode.description}` : ''
      }`;
      return {
        ...hsCode,
        fullName: label,
        value: hsCode.name,
        code: hsCode.name,
        label,
      } as CategoryHSCodeSuggested & CategoryHSOption;
    });
  }, [hsCodeSuggestions]);

  const handleKeyDownInput = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      e.preventDefault();
      setComponentStateDropdown();
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.stopPropagation();
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (options.length) {
        const container = containerRef.current;
        if (container) {
          const firstOption = container.querySelector('button[role="option"]') as HTMLButtonElement;
          if (firstOption) {
            firstOption.focus();
          }
        }
      }
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      const nextOption = e.currentTarget.nextElementSibling as HTMLButtonElement;
      if (nextOption && nextOption.getAttribute('role') === 'option') {
        nextOption.focus();
      } else {
        refInput.current?.focus();
        refInput.current?.select();
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      const prevOption = e.currentTarget.previousElementSibling as HTMLButtonElement;
      if (prevOption && prevOption.getAttribute('role') === 'option') {
        prevOption.focus();
      } else {
        refInput.current?.focus();
        refInput.current?.select();
      }
    }
  };

  return (
    <div
      className={twMerge(
        'w-fit max-w-[500px] min-w-[460px] overflow-y-auto flex flex-col',
        className
      )}
      role="combobox"
      aria-expanded="true"
      aria-haspopup="listbox"
      aria-controls="hs-code-listbox"
      ref={containerRef}
    >
      <div
        className={twMerge(
          'px-1 border-sky-300',
          portalPosition.direction === PortalVerticalDirection.UP ? 'border-t' : 'border-b'
        )}
      >
        <Input
          ref={refInput}
          className="border-none"
          LeadingIcon={Search}
          TrailingIcon={TrailingIcon}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDownInput}
        />
      </div>
      <div className="max-h-[300px] overflow-y-auto relative" role="listbox">
        <Pulse show={isLoadingHSCodeSuggestions} />
        {!options.length ? (
          <div className="px-4 py-4 text-ink-100 text-base">
            {(!isNoResults && <FormattedMessage id="shipments.category-hs-codes.tips" />) ||
              (!isLoadingHSCodeSuggestions ? (
                <FormattedMessage id="shipments.category-hs-codes.no-results" />
              ) : (
                <>&nbsp;</>
              ))}
          </div>
        ) : (
          options.map((opt) => (
            <button
              key={opt.code}
              type="button"
              role="option"
              aria-selected={selectedValue?.value === opt.value}
              className={twMerge(
                'w-full text-left py-2 text-base px-4 cursor-pointer text-ink-900 hover:bg-teal-100 transition-colors focus:bg-teal-200 outline-none whitespace-nowrap overflow-hidden text-ellipsis',
                selectedValue?.value === opt.value &&
                  'bg-teal-500 text-white hover:bg-teal-500 focus:bg-teal-500'
              )}
              onClick={() => {
                onSelect(opt);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(opt);
                  return;
                }
                handleOptionKeyDown(e);
              }}
            >
              <div className="w-full">
                <HighlightedHSCode code={opt.code} searchTerm={searchValue} />
                {opt.description && <span className="ml-1 text-wrap">- {opt.description}</span>}
              </div>
            </button>
          ))
        )}
      </div>
      {!isSearching && (
        <Button
          flat
          className={twMerge(
            'flex items-center gap-2 border-sky-300 justify-start',
            portalPosition.direction === PortalVerticalDirection.UP ? 'border-t' : 'border-b'
          )}
          onClick={setComponentStateDropdown}
          color="primary"
        >
          <ChevronBackward />
          <FormattedMessage id="shipments.category-hs-codes.back-to-category" />
        </Button>
      )}
    </div>
  );
}

export default CategoryHSCodeSearch;
